import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { startOfToday } from 'date-fns';

import Select from '../../juristec-ui/core/SelectNew';
import Button from '../../juristec-ui/core/Button';
import IconButton from '../../juristec-ui/core/IconButton';
import Drawer from '../../juristec-ui/core/Drawer';
import Popover from '../../juristec-ui/core/Popover';
import Tooltip from '../../juristec-ui/core/Tooltip';
import List from '../../juristec-ui/core/List';
import ListItem from '../../juristec-ui/core/ListItem';
import Checkbox from '../../juristec-ui/core/Checkbox';
import FilterItem from '../BigDataFilter';
import FilterApplied from '../BigDataFilterApplied';

import useToggleState from '../../juristec-ui/hooks/useToggleState';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';
import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import { verifyInput } from '../../juristec-ui/utils/validators/inputTextValidators';

import { History } from '../../juristec-ui/icons';

import {
  InternalDrawer,
  DrawerContainer,
  DrawerHeader,
  InputWrapper,
  AppliedList,
} from './styled/PublicationsFilter.styled';

const filterOpts = [
  // FilterByText
  { label: 'Termo', value: 'termo', id: 'termo' },
  { label: 'Termo negativo', value: 'termo_negativo', id: 'termo_negativo' },
  // FilterByDateYear
  // { label: 'Ano no CNJ', value: 'ano', id: 'ano' },
  // FilterByDateInterval
  { label: 'Publicação', value: 'publicacao', id: 'publicacao' },
  { label: 'Distribuição', value: 'distribuicao', id: 'distribuicao' },
  // FilterByOptionsSimple
  { label: 'Tribunal', value: 'tribunal', id: 'tribunal' },
];

const initialInfo = (key) => {
  switch (key) {
    case 'tribunal':
      return { value: [], error: true, errorMsg: '' };
    case 'distribuicao':
    case 'publicacao':
      return { value: [null, startOfToday()], error: false };
    case 'ano':
      return { value: new Date(), error: false, errorMsg: '' };
    case 'termo':
      return { value: '', error: true, errorMsg: '' };
    default:
      return { value: '', error: true, errorMsg: '' };
  }
};

const isRepeatableFilter = (key) => (['termo', 'termo_negativo'].includes(key));

const publicationsFilterDefaultPropLastQueries = [];
const publicationsFilterDefaultPropFilterFieldsState = [{}, () => {}];
const publicationsFilterDefaultPropDrawerState = [false, () => {}];
const publicationsFilterDefaultPropIsDirtyState = [false, () => {}];

const PublicationsFilter = ({
  lastQueries = publicationsFilterDefaultPropLastQueries,
  drawerState = publicationsFilterDefaultPropDrawerState,
  filterFieldsState = publicationsFilterDefaultPropFilterFieldsState,
  isDirtyState = publicationsFilterDefaultPropIsDirtyState,
  sendQuery,
  getOptions,
  isMobileSize = false,
}) => {
  const [showDrawer, closeDrawer] = drawerState;
  const [history, toggleHistory, closeHistory] = useToggleState(false);
  const [filterFields, setFilterFields] = filterFieldsState;
  const [isDirty, setIsDirty] = isDirtyState;
  const [filteredOpts, setFilteredOpts] = useState(filterOpts);
  const [tempFilter, setTempFilter] = useState({
    filterBy: filterOpts[0],
    ...initialInfo(filterOpts[0].value),
  });

  const DrawerComponent = useMemo(() => (
    isMobileSize ? Drawer : InternalDrawer
  ), [isMobileSize]);

  useEffect(() => {
    setFilteredOpts(() => {
      const auxF = filterOpts.filter((f) => {
        if (isRepeatableFilter(f.value)) return true;
        return !Object.keys(filterFields).includes(f.value);
      });
      const selected = auxF.find((f) => tempFilter.filterBy.value === f.value);
      setTempFilter((o) => ({
        filterBy: selected || (auxF[0] || {}),
        ...initialInfo(selected?.value || auxF[0]?.value),
        options: selected && o.options ? new Map([['tribunal', o.options.get('tribunal')]]) : undefined,
      }));
      return auxF;
    });
  }, [filterFields]);

  const addFilterField = () => {
    setIsDirty(true);
    const key = tempFilter.filterBy.value;
    const isRepeatable = isRepeatableFilter(key);

    setFilterFields((o) => {
      let auxVal;
      if (isRepeatable) {
        auxVal = o[key]?.value || {};
        auxVal[uuidv4()] = tempFilter.value;
      } else {
        auxVal = tempFilter.value;
      }
      return ({
        ...o,
        [key]: {
          label: tempFilter.filterBy.label,
          value: auxVal,
        },
      });
    });
  };

  const handleCheckBox = (check) => {
    setIsDirty(true);
    setFilterFields((o) => ({
      ...o,
      unicos: check,
    }));
  };

  const handleEditFilter = (field, subField, filterData) => {
    setIsDirty(true);
    const isRepeatable = isRepeatableFilter(field);
    setFilterFields((o) => {
      let auxVal;
      if (isRepeatable) {
        auxVal = o[field]?.value || {};
        auxVal[subField] = filterData.value;
      } else {
        auxVal = filterData.value;
      }
      return ({
        ...o,
        [field]: {
          ...o[field],
          value: auxVal,
        },
      });
    });
  };

  const handleRemoveFilter = (field, subField) => {
    setIsDirty(true);
    const isRepeatable = isRepeatableFilter(field);
    setFilterFields((o) => {
      if (isRepeatable) {
        const { [subField]: _, ...newValue } = o[field].value;
        if (Object.keys(newValue).length > 0) {
          return { ...o, [field]: { ...o[field], value: newValue } };
        }
      }
      const { [field]: _, ...newObj } = o;
      return newObj;
    });
  };

  const handleFilterBy = async (selected) => {
    switch (selected.value) {
      case 'tribunal': {
        const opts = await getOptions(selected.value);
        const optsList = opts.map((dt) => ({ label: dt.toUpperCase(), value: dt, id: dt }));
        setTempFilter({
          filterBy: selected,
          ...initialInfo(selected.value),
          options: optsList,
        });
        break;
      }
      default: {
        setTempFilter({
          filterBy: selected,
          ...initialInfo(selected.value),
        });
        break;
      }
    }
  };

  const handleValue = async (value, key, subKey, stateSetter) => {
    switch (key) {
      case 'tribunal': {
        const errorMsg = value?.length > 0 ? '' : 'Selecione pelo menos uma opção';
        return stateSetter((o) => ({
          ...o, value, error: errorMsg.length !== 0, errorMsg,
        }));
      }
      case 'distribuicao':
      case 'publicacao': {
        const error = (value[0] === null && value[1] === null) || (
          ((!(value[0] === null) === !(value[1] === null)) && value[0] > value[1])
        );
        return stateSetter((o) => ({
          ...o, value, error,
        }));
      }
      default: {
        const errorMsg = typeof value === 'string' ? verifyInput(value, true, 3) : '';
        return stateSetter((o) => ({
          ...o, value, error: errorMsg.length !== 0, errorMsg,
        }));
      }
    }
  };

  const handleNewValue = (value, subKey) => {
    handleValue(value, tempFilter.filterBy.value, subKey, setTempFilter);
  };

  const handleEditValue = (value, key, subKey, stateSetter) => {
    handleValue(value, key, subKey, stateSetter);
  };

  const loadFilters = (index) => {
    setIsDirty(true);
    const { timestamp: _, ...newObj } = lastQueries[index];
    setFilterFields(newObj);
  };

  const handleSendQuery = async () => {
    setIsDirty(false);
    setIsDirty(await sendQuery());
  };

  return (
    <DrawerComponent
      open={showDrawer}
      direction="right"
      handleCloseDrawer={closeDrawer}
    >
      <DrawerContainer>
        <DrawerHeader>
          <h2>Busca</h2>
          <Popover
            style={{
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            offset={[0, 0]}
            open={history}
            stayOpened={false}
            closePopover={closeHistory}
            atModal
          >
            <Popover.Action>
              <Tooltip text="Últimas buscas" atModal>
                <IconButton
                  onClick={toggleHistory}
                  disabled={!lastQueries?.length > 0}
                >
                  <History />
                </IconButton>
              </Tooltip>
            </Popover.Action>
            <Popover.Content>
              <List>
                {lastQueries.map((q, i) => (
                  <ListItem
                    key={q.timestamp}
                    onClick={() => loadFilters(i)}
                    noOutline
                  >
                    {formatDateTime(q.timestamp, { time: 'half' })}
                  </ListItem>
                ))}
              </List>
            </Popover.Content>
          </Popover>
        </DrawerHeader>
        <InputWrapper
          style={filterFields.termo ? {} : { opacity: '0.5', cursor: 'not-allowed' }}
        >
          <Select
            label="Buscar por"
            options={filteredOpts}
            value={tempFilter.filterBy}
            onChange={handleFilterBy}
            wrapperStyle={filterFields.termo ? {} : { pointerEvents: 'none' }}
            error={!filterFields.termo}
            errorMessage="É necessário pelo menos 1 termo para a busca"
          />
        </InputWrapper>
        <InputWrapper>
          <FilterItem
            field={tempFilter.filterBy?.value}
            label={tempFilter.filterBy?.label}
            value={tempFilter.value}
            errorMsg={tempFilter.errorMsg}
            options={tempFilter.options}
            handle={handleNewValue}
          />
        </InputWrapper>
        <InputWrapper style={{ marginTop: '.5rem' }}>
          <Button
            color="secondary"
            style={{ width: '100%' }}
            disabled={tempFilter.error}
            onClick={addFilterField}
          >
            Adicionar campo
          </Button>
        </InputWrapper>
        {Object.keys(filterFields).length > 1 && (
          <>
            <h4>Campos aplicados:</h4>
            <AppliedList>
              {Object.keys(filterFields).map((fField) => (
                isRepeatableFilter(fField) ? (
                  Object.entries(filterFields[fField].value).map(([vKey, vVal]) => (
                    <FilterApplied
                      key={vKey}
                      field={fField}
                      subField={vKey}
                      label={filterFields[fField].label}
                      value={vVal}
                      removeFilter={handleRemoveFilter}
                      handleValue={handleEditValue}
                      getOptions={getOptions}
                      editFilter={handleEditFilter}
                    />
                  ))
                ) : fField !== 'unicos' && (
                  <FilterApplied
                    key={fField}
                    field={fField}
                    label={filterFields[fField].label}
                    value={filterFields[fField].value}
                    removeFilter={handleRemoveFilter}
                    handleValue={handleEditValue}
                    getOptions={getOptions}
                    editFilter={handleEditFilter}
                  />
                )
              ))}
            </AppliedList>
          </>
        )}
        <InputWrapper style={{ marginTop: 'auto', position: 'sticky', padding: '.4rem' }}>
          <Checkbox
            text="Apenas CNJs únicos"
            checked={filterFields.unicos}
            handleCheckboxChange={handleCheckBox}
            style={{ whiteSpace: 'nowrap', marginBottom: '.4rem' }}
          />
          <Button
            color="primary"
            style={{ width: '100%' }}
            disabled={!isDirty || !filterFields.termo}
            onClick={handleSendQuery}
          >
            Buscar
          </Button>
        </InputWrapper>
      </DrawerContainer>
    </DrawerComponent>
  );
};

PublicationsFilter.propTypes = {
  lastQueries: PropTypes.arrayOf(PropTypes.shape({
    timestamp: PropTypes.string,
  })),
  filterFieldsState: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.func,
  ])),
  drawerState: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ])),
  isDirtyState: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ])),
  getOptions: PropTypes.func.isRequired,
  sendQuery: PropTypes.func.isRequired,
  isMobileSize: PropTypes.bool,
};

export default PublicationsFilter;
